import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ArrowLeftOutline,
	Button,
	EyeOutline,
	FormControl,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spacer,
	Text,
	useToast,
} from '@bonusly/design-system';
import { Schema } from '@bonusly/schema';

import { Reveal } from '../../components/display/reveal';
import { InfoLabel } from '../../components/form/info-label';
import { UserEmailSelector } from '../../components/form/search/user-email-selector';
import { Celebration } from '../../entities/celebrations';
import { User } from '../../entities/users';
import { useGiveCelebration } from '../../queries/celebrations';
import { loadSearchUsers, RuleCondition } from '../../queries/users';
import { FeedPreview } from './feed-preview';

type SendManualModalProps = {
	celebration: Celebration;
	isOpen: boolean;
	onClose: () => void;
};

enum Stage {
	Form,
	Preview,
}

export const SendManualModal: FC<SendManualModalProps> = ({ celebration, isOpen, onClose }) => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'admin' });
	const toast = useToast();
	const [manualUsers, setManualUsers] = useState<User[]>([]);
	const [stage, setStage] = useState(Stage.Form);

	const { mutate } = useGiveCelebration({
		onSuccess: () => {
			setManualUsers([]);
			onClose();
			toast({
				title: t('sendManualSuccess', { name: manualUsers[0].displayName }),
				status: 'success',
			});
		},
		onError: () => {
			toast({
				title: t('sendManualFailure'),
				status: 'error',
			});
		},
	});

	const handleSearch = async (query: string) => {
		const conditions = celebration.receivingRulesGroup ? Schema.serialize<RuleCondition[]>(celebration.receivingRulesGroup.conditions) : [];
		const users = await loadSearchUsers({ search: query, conditions, operator: 'or' });
		return users.filter((user) => manualUsers.every((u) => u.id !== user.id));
	};

	const handleReset = () => {
		setManualUsers([]);
		setStage(Stage.Form);
	};

	const user = manualUsers[0] || ({} as any);

	return (
		<Modal isOpen={isOpen} onCloseComplete={handleReset} onClose={onClose} size="2xl">
			<ModalOverlay />
			<ModalContent>
				<Reveal if={stage === Stage.Form}>
					<ModalHeader>{t('sendManualTitle')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody p={5}>
						<Text mb={2}>{t('sendManualDescription1', { type: celebration.type })}</Text>
						<Text size="sm" color="text.softest" mb={4}>
							{t('sendManualDescription2', { type: celebration.type })}
						</Text>

						<FormControl>
							<InfoLabel label={t('sendManualTooltip')}>{t('sendManualRecipientLabel', { type: celebration.type })}</InfoLabel>
							<UserEmailSelector limit={1} onChange={setManualUsers} onSearch={handleSearch} value={manualUsers} />
						</FormControl>
					</ModalBody>

					<ModalFooter gap={2}>
						<Button variant="ghost" onClick={onClose}>
							{t('sendManualCancel')}
						</Button>
						<Spacer />
						<Button disabled={manualUsers.length === 0} leftIcon={<EyeOutline />} onClick={() => setStage(Stage.Preview)} variant="ghost">
							{t('sendManualPreview')}
						</Button>
						<Button disabled={manualUsers.length === 0} onClick={() => mutate({ celebrationId: celebration.id, userIds: manualUsers.map((user) => user.id) })}>
							{t('sendManualConfirm')}
						</Button>
					</ModalFooter>
				</Reveal>

				<Reveal if={stage === Stage.Preview}>
					<ModalHeader>
						<IconButton
							aria-label={t('sendManualBack')}
							bgColor="grayAlpha.100"
							boxSize={8}
							isRound
							mr={4}
							onClick={() => setStage(Stage.Form)}
							size="sm"
							_hover={{
								bgColor: 'grayAlpha.150',
							}}
						>
							<ArrowLeftOutline size="sm" />
						</IconButton>
						{t('sendManualPreviewTitle')}
					</ModalHeader>
					<ModalCloseButton />

					<ModalBody p={5}>
						<FeedPreview
							amount={celebration.amount}
							iconURL={celebration.iconUrl}
							imageURL={celebration.imageUrl}
							headline={celebration.headline}
							reason={celebration.reason}
							type={celebration.type}
							username={user.username}
							userImage={user.profilePic}
							userDisplayName={user.displayName}
						/>
					</ModalBody>
				</Reveal>
			</ModalContent>
		</Modal>
	);
};
